import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError } from '../../api';
import { seasonEndpoints } from '../../api-seasons';
import type { GamePricePlan, PriceEditInput, PriceOperation } from '../../api-seasons';
import { Badge, Button, ErrorNote, Field, Panel } from '../../ui';
import { INPUT, dollars, toCents } from '../event/shared';
import { QuickSelect, chunks, gameWhen } from './QuickSelect';

/**
 * The season's prices on one grid (D-371).
 *
 * Rows are games; columns are a price zone and a ticket type, the same codes
 * on every game because every game was copied from one template. Choose
 * games and columns, say what to do — set to an amount, move by an amount, or
 * copy another game's prices — and preview old → new per cell. Applying gives
 * each changed game a new price-table version (active ones are locked); a game
 * never priced live has its draft edited instead. Any game can still be
 * repriced by hand on its own Pricing tab.
 */

const GROUP = 20;

export function PricesPanel({ seasonId, onChanged }: { seasonId: string; onChanged: () => void }) {
  const grid = useQuery({ queryKey: ['season-prices', seasonId], queryFn: () => seasonEndpoints.prices(seasonId) });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [columns, setColumns] = useState<Set<string> | null>(null);
  const [kind, setKind] = useState<PriceOperation['kind']>('SET');
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<1 | -1>(1);
  const [copyFrom, setCopyFrom] = useState('');
  const [includeTiered, setIncludeTiered] = useState(false);
  const [activate, setActivate] = useState(true);
  const [reason, setReason] = useState('');
  const [plans, setPlans] = useState<Map<string, GamePricePlan> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const allColumns = grid.data?.columns ?? [];
  const chosenColumns = columns ?? new Set(allColumns.map((c) => c.key));
  const cents = toCents(amount);
  const operation = (): PriceOperation | null => {
    if (kind === 'COPY') return copyFrom ? { kind, from_event_id: copyFrom } : null;
    if (cents === null) return null;
    return kind === 'SET' ? { kind, cents } : { kind, cents: cents * direction };
  };
  const body = (ids: string[], dryRun: boolean): PriceEditInput => ({
    selection: { event_ids: ids },
    columns: [...chosenColumns],
    operation: operation()!,
    include_tiered: includeTiered,
    activate,
    reason: reason.trim() || null,
    dry_run: dryRun,
  });

  const preview = useMutation({
    mutationFn: () => seasonEndpoints.editPrices(seasonId, body([...selected], true)),
    onSuccess: (result) => {
      setPlans(new Map(result.games.map((plan) => [plan.event_id, plan])));
      setError(null);
      setNotice(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const apply = useMutation({
    mutationFn: async () => {
      let changed = 0;
      for (const group of chunks([...selected], GROUP)) {
        const result = await seasonEndpoints.editPrices(seasonId, body(group, false));
        changed += result.games.filter((plan) => plan.result).length;
      }
      return changed;
    },
    onSuccess: async (changed) => {
      setPlans(null);
      setNotice(`${changed} ${changed === 1 ? 'game' : 'games'} repriced${activate ? '' : ' as drafts; activate them on each game’s Pricing tab or with Publish'}.`);
      await grid.refetch();
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const rows = grid.data?.games ?? [];
  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
    setPlans(null);
  };
  const changing = plans ? [...plans.values()].filter((plan) => plan.action === 'NEW_VERSION' || plan.action === 'EDIT_DRAFT').length : 0;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel title="Season prices" description="Each game’s base price per zone and ticket type (no tier, every channel). Draft means the game has never had live prices and its draft table is shown.">
        <QuickSelect games={rows} onSelect={(ids) => { setSelected(ids); setPlans(null); }} />
        <div className="overflow-x-auto border-t border-line">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-3 py-2" />
                <th className="px-3 py-2 text-xs font-semibold tracking-wide text-ink-400 uppercase">Game</th>
                {allColumns.map((column) => (
                  <th key={column.key} className="px-3 py-2 text-right text-xs font-semibold tracking-wide text-ink-400 uppercase">
                    <label className="inline-flex items-center gap-1" title={`${column.zone_name} · ${column.type_name}`}>
                      <input
                        type="checkbox"
                        checked={chosenColumns.has(column.key)}
                        onChange={() => {
                          const next = new Set(chosenColumns);
                          if (next.has(column.key)) next.delete(column.key);
                          else next.add(column.key);
                          setColumns(next);
                          setPlans(null);
                        }}
                      />
                      {column.zone_code} · {column.type_code}
                    </label>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={allColumns.length + 2} className="px-5 py-10 text-center text-sm text-ink-400">
                    {grid.isLoading ? 'Loading…' : 'No games in this season yet.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const plan = plans?.get(row.event_id);
                  return (
                    <tr key={row.event_id} className="border-b border-line align-top last:border-0">
                      <td className="px-3 py-2">
                        <input type="checkbox" aria-label={`Choose ${row.title}`} checked={selected.has(row.event_id)} onChange={() => toggle(row.event_id)} />
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/events/${row.event_id}`} className="text-ink-900 hover:underline">
                          {row.title}
                        </Link>
                        <div className="text-xs text-ink-400">
                          {gameWhen(row.starts_at, row.timezone)}
                          {row.version ? ` · v${row.version.version}` : ''} {row.version?.status === 'DRAFT' ? <Badge tone="warn">draft</Badge> : null}
                          {row.draft_in_progress ? <Badge tone="warn">draft in progress</Badge> : null}
                          {row.other_prices ? ` · ${row.other_prices} tiered or channel prices` : ''}
                        </div>
                        {plan?.reason ? <div className="text-xs text-bad">{plan.reason}</div> : null}
                      </td>
                      {allColumns.map((column) => {
                        const cell = row.cells[column.key];
                        const change = plan?.changes.find((c) => c.key === column.key);
                        return (
                          <td key={column.key} className="tabular px-3 py-2 text-right whitespace-nowrap">
                            {change ? (
                              <span>
                                <span className="text-ink-400 line-through">{dollars(change.from?.face_value_cents)}</span> <span className="font-semibold text-ink-900">{dollars(change.to.face_value_cents)}</span>
                              </span>
                            ) : (
                              <span className="text-ink-600">{cell ? dollars(cell.face_value_cents) : '—'}</span>
                            )}
                            {cell && cell.list_price_cents < cell.face_value_cents && !change ? <div className="text-xs text-ink-400">sells at {dollars(cell.list_price_cents)}</div> : null}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Change prices" description={`${selected.size} ${selected.size === 1 ? 'game' : 'games'} and ${chosenColumns.size} ${chosenColumns.size === 1 ? 'column' : 'columns'} chosen. A standing offer (a price below face) keeps its size when the face moves.`}>
        <div className="flex flex-wrap items-end gap-4 px-5 py-4">
          <Field label="Do" htmlFor="price-kind">
            <select id="price-kind" value={kind} onChange={(e) => { setKind(e.target.value as PriceOperation['kind']); setPlans(null); }} className={INPUT}>
              <option value="SET">Set to</option>
              <option value="ADJUST">Raise or lower by</option>
              <option value="COPY">Copy prices from game</option>
            </select>
          </Field>
          {kind === 'COPY' ? (
            <Field label="Game" htmlFor="price-copy">
              <select id="price-copy" value={copyFrom} onChange={(e) => { setCopyFrom(e.target.value); setPlans(null); }} className={`w-72 ${INPUT}`}>
                <option value="">Choose…</option>
                {rows.map((row) => (
                  <option key={row.event_id} value={row.event_id}>
                    {row.local_date} {row.title}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <>
              {kind === 'ADJUST' ? (
                <Field label="Direction" htmlFor="price-direction">
                  <select id="price-direction" value={direction} onChange={(e) => { setDirection(Number(e.target.value) as 1 | -1); setPlans(null); }} className={INPUT}>
                    <option value={1}>+ raise</option>
                    <option value={-1}>− lower</option>
                  </select>
                </Field>
              ) : null}
              <Field label="Amount" htmlFor="price-amount">
                <input id="price-amount" value={amount} onChange={(e) => { setAmount(e.target.value); setPlans(null); }} placeholder="12.00" inputMode="decimal" className={`w-28 ${INPUT}`} />
              </Field>
            </>
          )}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={includeTiered} onChange={(e) => { setIncludeTiered(e.target.checked); setPlans(null); }} disabled={kind === 'COPY'} />
            Tiered and channel prices too
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={activate} onChange={(e) => setActivate(e.target.checked)} />
            Activate the new prices
          </label>
          <Field label="Reason" htmlFor="price-reason">
            <input id="price-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Season price edit" maxLength={500} className={`w-56 ${INPUT}`} />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3">
          <Button variant="primary" disabled={selected.size === 0 || chosenColumns.size === 0 || !operation() || preview.isPending} onClick={() => preview.mutate()}>
            {preview.isPending ? 'Checking…' : 'Preview'}
          </Button>
          <Button disabled={!plans || changing === 0 || apply.isPending} onClick={() => apply.mutate()}>
            {apply.isPending ? 'Applying…' : `Apply to ${changing} ${changing === 1 ? 'game' : 'games'}`}
          </Button>
          {plans ? (
            <span className="text-sm text-ink-600">
              {changing} to change; {[...plans.values()].filter((p) => p.action === 'UNCHANGED').length} unchanged; {[...plans.values()].filter((p) => p.action === 'SKIPPED').length} skipped. The grid shows old → new.
            </span>
          ) : null}
          {notice ? <span className="text-sm text-ok">{notice}</span> : null}
        </div>
      </Panel>
    </div>
  );
}
